import React, { useState, useEffect, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Bell, ChevronDown, Settings as SettingsIcon, LogOut, User as UserIcon } from 'lucide-react';
import { auth, firestore as db } from '../config/Config';
import './Header.css';

function Header() {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [userInfo, setUserInfo] = useState(() => {
    const saved = JSON.parse(localStorage.getItem('userDetails') || '{}');
    return {
      name: saved?.name || '',
      email: saved?.email || '',
      designation: saved?.designation || saved?.role || '',
    };
  });

  const currentAuthUser = auth.currentUser;

  // Sync user info from Firestore and auth live
  useEffect(() => {
    const syncUser = () => {
      const saved = JSON.parse(localStorage.getItem('userDetails') || '{}');
      setUserInfo(prev => ({
        name: saved?.name || currentAuthUser?.displayName || prev.name,
        email: saved?.email || currentAuthUser?.email || prev.email,
        designation: saved?.designation || saved?.role || prev.designation,
      }));
    };

    window.addEventListener('storage', syncUser);

    const uid = currentAuthUser?.uid;
    let unsubscribe = () => {};
    if (uid) {
      unsubscribe = db.collection('users').doc(uid).onSnapshot((doc) => {
        if (doc.exists) {
          const d = doc.data();
          const name = d.name || currentAuthUser?.displayName || '';
          const email = d.email || currentAuthUser?.email || '';
          const designation = d.designation || d.role || '';
          setUserInfo({ name, email, designation });

          const local = JSON.parse(localStorage.getItem('userDetails') || '{}');
          localStorage.setItem('userDetails', JSON.stringify({ ...local, name, email, designation }));
        }
      });
    }

    return () => {
      window.removeEventListener('storage', syncUser);
      unsubscribe();
    };
  }, [currentAuthUser]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayName = userInfo.name || currentAuthUser?.displayName || currentAuthUser?.email?.split('@')[0] || 'User';
  const displayEmail = userInfo.email || currentAuthUser?.email || '';
  const displayRole = userInfo.designation;

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : name.charAt(0).toUpperCase();
  };

  const handleLogOut = () => {
    setDropdownOpen(false);
    Swal.fire({
      title: "Sign Out?",
      text: "Are you sure you want to end your session?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ea6e2b",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Sign Out",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.clear();
        auth.signOut().then(() => {
          navigate('/login');
        });
      }
    });
  };

  return (
    <Row className="m-0" style={{ position: 'sticky', top: 0, zIndex: 100 }}>
      <Col sm={1}></Col>
      <Col sm={11} style={{ padding: 0 }}>
        <header style={{
          height: '68px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #ede8e1',
          padding: '0 36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          boxShadow: '0 1px 4px rgba(0,0,0,0.02)'
        }}>
          {/* Header Right Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '22px' }}>
            {/* Notification bell */}
            <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => navigate('/notification')}>
              <Bell size={19} color="#475569" />
              <span style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                backgroundColor: '#ea6e2b',
                color: '#ffffff',
                fontSize: '9.5px',
                fontWeight: 700,
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 5px rgba(234,110,43,0.4)'
              }}>
                3
              </span>
            </div>

            {/* User Profile Pill with Dropdown */}
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  padding: '5px 10px',
                  borderRadius: '12px',
                  transition: 'background-color 0.2s ease',
                  backgroundColor: dropdownOpen ? '#f8fafc' : 'transparent'
                }}
                onClick={() => setDropdownOpen(prev => !prev)}
                title="Account Menu"
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#ea6e2b',
                  color: '#ffffff',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13.5px',
                  boxShadow: '0 2px 8px rgba(234,110,43,0.3)'
                }}>
                  {getInitials(displayName)}
                </div>
                <div style={{ textAlign: 'left', lineHeight: 1.25 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                    {displayName}
                  </div>
                  {displayRole && (
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {displayRole}
                    </div>
                  )}
                </div>
                <ChevronDown
                  size={14}
                  color="#64748b"
                  style={{
                    transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                />
              </div>

              {/* Production-Ready Profile Dropdown Menu */}
              {dropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  width: '240px',
                  backgroundColor: '#ffffff',
                  borderRadius: '14px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                  padding: '8px',
                  zIndex: 1000,
                  animation: 'fadeIn 0.15s ease-out'
                }}>
                  {/* User info preview in dropdown */}
                  <div style={{ padding: '10px 12px 12px', borderBottom: '1px solid #f1f5f9', marginBottom: '6px' }}>
                    <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#0f172a' }}>
                      {displayName}
                    </div>
                    {displayEmail && (
                      <div style={{ fontSize: '11.5px', color: '#64748b', wordBreak: 'break-all', marginTop: '2px' }}>
                        {displayEmail}
                      </div>
                    )}
                    {displayRole && (
                      <span style={{
                        display: 'inline-block',
                        marginTop: '6px',
                        backgroundColor: '#fff7ed',
                        color: '#ea6e2b',
                        fontSize: '10.5px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '6px',
                        border: '1px solid #ffedd5'
                      }}>
                        {displayRole}
                      </span>
                    )}
                  </div>

                  {/* Links */}
                  <Link
                    to="/settings"
                    onClick={() => setDropdownOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      color: '#334155',
                      textDecoration: 'none',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <SettingsIcon size={15} color="#ea6e2b" />
                    <span>Account Settings</span>
                  </Link>

                  <div style={{ height: '1px', backgroundColor: '#f1f5f9', margin: '6px 0' }} />

                  {/* Sign Out Button */}
                  <button
                    type="button"
                    onClick={handleLogOut}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      color: '#ef4444',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <LogOut size={15} color="#ef4444" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
      </Col>
    </Row>
  );
}

export default Header;
